import React, { useState } from "react";
import { Menu, X } from "lucide-react";
import axios from "axios";

// Layout
import DashboardSidebar from "../../components/dashboard components/DashboardSidebar";
import DashboardHeader from "../../components/dashboard components/DashboardHeader";

// Forms
import ProjectHeroSectionForm from "../../components/dashboard components/forms/ProjectHeroSectionForm";
import AboutForm from "../../components/dashboard components/forms/AboutForm";

// ✅ Use environment variable for API base
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Projects = () => {
  const [activeSection, setActiveSection] = useState("projects");
  const [activeTab, setActiveTab] = useState("hero");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // ===================
  // Data States
  // ===================
  const [aboutData, setAboutData] = useState({
    subtitle: "",
    title: "",
    description: "",
    button: "",
    image: "",
  });

  // ===================
  // Save Handlers (API-ready)
  // ===================
  const handleSave = async (sectionName, data) => {
    try {
      await axios.post(`${API_BASE_URL}/projects/update`, {
        section: sectionName,
        data,
      });
      alert(`${sectionName} saved successfully!`);
    } catch (err) {
      console.error(`Error saving ${sectionName}:`, err);
      alert(`Failed to save ${sectionName}. Check console for details.`);
    }
  };

  // ===================
  // Tabs Renderer
  // ===================
  const renderActiveTab = () => {
    switch (activeTab) {
      case "hero":
        return (
          <ProjectHeroSectionForm
            apiBase={`${API_BASE_URL}/projects`}
            token={localStorage.getItem("token")}
          />
        );

      case "about":
        return (
          <AboutForm
            aboutData={aboutData}
            setAboutData={setAboutData}
            onSave={() => handleSave("About", aboutData)}
          />
        );

      default:
        return null;
    }
  };

  // ===================
  // Tab Button Component
  // ===================
  const TabButton = ({ id, label }) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`px-6 py-3 text-sm md:text-base font-semibold border-b-4 transition-all duration-300 ${
        activeTab === id
          ? "border-blue-600 text-blue-700 bg-blue-50"
          : "border-transparent text-gray-600 hover:text-blue-600 hover:bg-gray-50"
      }`}
    >
      {label}
    </button>
  );

  // ===================
  // Return Component
  // ===================
  return (
    <div className="flex min-h-screen bg-gray-50 font-sans">
      {/* Sidebar - Desktop */}
      <div className="hidden lg:block">
        <DashboardSidebar
          activeSection={activeSection}
          setActiveSection={setActiveSection}
        />
      </div>

      {/* Sidebar - Mobile */}
      <div
        className={`fixed inset-y-0 left-0 bg-white w-72 shadow-2xl transform transition-transform duration-300 ease-in-out z-50 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } lg:hidden`}
      >
        <DashboardSidebar
          activeSection={activeSection}
          setActiveSection={setActiveSection}
        />
        <button
          className="absolute top-4 right-4 text-gray-500 hover:text-blue-600"
          onClick={() => setSidebarOpen(false)}
        >
          <X size={28} />
        </button>
      </div>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Main Content */}
      <main className="flex-1 flex flex-col w-full">
        {/* Header */}
        <div className="sticky top-0 bg-white z-30 shadow-sm flex items-center justify-between px-4 sm:px-8 py-4">
          <button
            className="lg:hidden text-gray-700 hover:text-blue-600"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu size={28} />
          </button>

          <div className="flex-1 flex justify-center">
            <DashboardHeader title="Projects Management" backTo="/dashboard" />
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white border-b border-gray-200 shadow-sm">
          <div className="flex justify-center gap-2 sm:gap-4 px-2 sm:px-4 py-2 flex-wrap">
            <TabButton id="hero" label="Hero Section" />
            <TabButton id="about" label="About Section" />
          </div>
        </div>

        {/* Active Form */}
        <div className="p-4 sm:p-8 md:p-10 max-w-6xl mx-auto w-full bg-white mt-4 sm:mt-6 rounded-xl shadow-md">
          {renderActiveTab()}
        </div>
      </main>
    </div>
  );
};

export default Projects;
